// Setup › Ads and tracking numbers on the SettingsPage pattern (BL-189): the call side first, then the ad
// money, one section each, in one column. Campaigns and the tracking numbers whose calls are credited to
// them (BL-008), what happens to a call nobody answers and the numbers turned away (BL-001, Forwarding.tsx's
// rules), the website tag (BL-092), the ad spend and the ad platforms' campaigns linked to the company's,
// conversions sent to Google Ads and how the ad accounts are doing (BL-091), and the monthly cap. Every
// add or change opens a FormSheet where the inline form was; a remove stays a row's action. While the
// company mirrors its import source the campaigns read, with the line (BL-088); the numbers stay ours.
// Riverstone keeps CampaignsTab.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { localDateOf, normalizePhone } from '@flowology/kernel';
import { AdsStatusProps } from '@flowology/ui-web';
import {
  adsHealthRows,
  adsRefusalRows,
  Button,
  Checkbox,
  Link,
  RowList,
  SelectField,
  TextField,
  type Row,
} from '@flowology/ui-web/fl';
import {
  FormSheet,
  SettingFieldLine,
  SettingFields,
  SettingRow,
  SettingsPage,
  SettingsSection,
} from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { allSpend, type SpendPage } from '../blueprint/ads-model.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { money } from '../review/review-model.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { phoneText } from '../types.ts';
import {
  blockedCount,
  CHANNEL_WORDS,
  CHANNELS,
  campaignMeans,
  campaignOrder,
  forwardMeans,
  numberMeans,
  unansweredMeans,
  type BlockedNumber,
  type Campaign,
  type Channel,
  type TrackingNumber,
  type Unanswered,
} from './campaigns-model.ts';
import {
  draftOf as mapDraftOf,
  KIND_WORDS,
  KINDS,
  mapFromWords,
  mapOf,
  switchWords,
  type MapDraft,
} from './conversions-model.ts';
import { LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { ruleSection, rulesCardLine, settingsRights } from './settings-model.ts';
import { setupRights } from './setup-model.ts';
import { SetupRuleRows } from './SetupRules.tsx';
import { useRuleData } from './rule-data.ts';
import {
  draftOf as siteDraftOf,
  siteInput,
  siteMeans,
  tagSnippet,
  weekLine,
  type SiteAnswer,
  type SiteDraft,
} from './site-model.ts';
import {
  centsTyped,
  editable,
  spendMeans,
  unlinkedMeans,
  type SpendEntry,
  type Unlinked,
} from './spend-model.ts';

/** The server's words, or the form's own (an amount that isn't one), for a sheet to show. */
const fail = (e: unknown): Error =>
  new Error(e instanceof RpcError ? refusal(e) : e instanceof Error ? e.message : refusal(e), { cause: e });
const say = (e: unknown) => fail(e).message;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const PAGE = 50;

/** A section's note: the mirror line, what was just done (status), or what failed (alert). */
function noteOf(...parts: { text: string | null; role?: 'status' | 'alert' }[]): { note?: ReactNode } {
  const shown = parts.filter((p) => p.text);
  if (!shown.length) return {};
  return {
    note: (
      <>
        {shown.map((p, i) => (
          <span key={i} {...(p.role ? { role: p.role } : {})}>
            {i ? ' ' : null}
            {p.text}
          </span>
        ))}
      </>
    ),
  };
}

/** One write from a row: what it says once done, or its refusal. */
function useWrite(after: () => void) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const write = (run: () => Promise<string>) => {
    setBusy(true);
    setFailed(null);
    run()
      .then((s) => {
        setSaid(s);
        after();
      })
      .catch((e: unknown) => setFailed(say(e)))
      .finally(() => setBusy(false));
  };
  return { busy, said, failed, write, setSaid };
}

export function AdsPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const setup = setupRights(held);
  const rights = settingsRights(held);
  const canEdit = setup.marketing;
  // the campaigns are the import source's while the company mirrors it; tracking numbers stay ours
  const mirroring = useMirror();
  const crumb = usePageCrumb(nav);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [numbers, setNumbers] = useState<TrackingNumber[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    void Promise.all([
      client.call<{ campaigns: Campaign[] }>('campaigns.list', { includeRetired: true }),
      client.call<{ numbers: TrackingNumber[] }>('trackingNumbers.list', {}),
    ])
      .then(([c, n]) => {
        setCampaigns(campaignOrder(c.campaigns));
        setNumbers(n.numbers);
      })
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const listed = { campaigns, numbers, error, load };
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('campaigns').label ?? 'Ads and tracking numbers',
            lede: LEDE.campaigns,
          }}
        >
          <Campaigns {...listed} canEdit={canEdit && !mirroring} mirroring={mirroring && canEdit} />
          <Numbers {...listed} canEdit={canEdit} />
          <WhenNobodyAnswers canEdit={canEdit} />
          <Blocked canEdit={canEdit} />
          <Website canEdit={canEdit} numbers={numbers ?? []} />
          <Spend canEdit={canEdit} campaigns={campaigns ?? []} />
          <Linked canEdit={canEdit} campaigns={campaigns ?? []} />
          <Conversions canEdit={canEdit} />
          <MonthlyCap rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

interface Listed {
  campaigns: Campaign[] | null;
  numbers: TrackingNumber[] | null;
  error: string | null;
  load: () => void;
}

const statusOf = <T,>(list: readonly T[] | null, error: string | null) =>
  list ? (list.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

// ── Campaigns ──

type CampaignDraft = { id?: string; name: string; channel: Channel; active: boolean; rowVersion?: number };

function Campaigns({
  campaigns,
  error,
  load,
  canEdit,
  mirroring,
}: Listed & { canEdit: boolean; mirroring: boolean }) {
  const [open, setOpen] = useState<CampaignDraft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const rows: Row[] = (campaigns ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    lines: [{ text: campaignMeans(c) }],
    ...(c.active ? {} : { off: true }),
    ...(canEdit ? { onOpen: () => setOpen({ ...c }), openLabel: `Change ${c.name}` } : {}),
  }));
  return (
    <>
      <SettingsSection
        title="Campaigns"
        line="Where calls and work come from. A call is credited to the campaign of the number it dialled; the office can book it under another when the caller says how they found you."
        {...(canEdit
          ? {
              action: (
                <Link onPress={() => setOpen({ name: '', channel: 'google_ads', active: true })}>
                  Add a campaign
                </Link>
              ),
            }
          : {})}
        {...noteOf({ text: mirroring ? MIRROR_LINE : null }, { text: said, role: 'status' })}
        status={statusOf(campaigns, error)}
        emptyText="No campaigns yet."
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        <RowList rows={rows} label="Campaigns" />
      </SettingsSection>
      {open && (
        <CampaignSheet
          key={open.id ?? 'new'}
          first={open}
          onSaved={(name) => {
            setSaid(`${name} saved.`);
            load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function CampaignSheet({
  first,
  onSaved,
  onClose,
}: {
  first: CampaignDraft;
  onSaved: (name: string) => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const save = async () => {
    try {
      const r = d.id
        ? await client.call<Campaign>('campaigns.update', {
            id: d.id,
            rowVersion: d.rowVersion,
            name: d.name.trim(),
            channel: d.channel,
            active: d.active,
          })
        : await client.call<Campaign>('campaigns.create', { name: d.name.trim(), channel: d.channel });
      onSaved(r.name);
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title={d.id ? `Change ${first.name}` : 'A new campaign'}
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      isSubmitDisabled={!d.name.trim()}
      onClose={onClose}
      onSubmit={save}
    >
      <TextField label="Name" maxLength={100} value={d.name} onChange={(name) => setD({ ...d, name })} />
      <SelectField
        label="Channel"
        value={d.channel}
        options={CHANNELS.map((ch) => ({ value: ch, label: CHANNEL_WORDS[ch] }))}
        onChange={(v) => setD({ ...d, channel: v as Channel })}
      />
      {d.id && (
        <Checkbox isSelected={!d.active} onChange={(retired) => setD({ ...d, active: !retired })}>
          Retired: calls and jobs keep it, but nobody books under it again
        </Checkbox>
      )}
    </FormSheet>
  );
}

// ── Tracking numbers ──

type NumberDraft = { phone: string; label: string; campaignId: string; forwardTo: string; isNew: boolean };

function Numbers({ campaigns, numbers, error, load, canEdit }: Listed & { canEdit: boolean }) {
  const [open, setOpen] = useState<NumberDraft | null>(null);
  const { busy, said, failed, write, setSaid } = useWrite(load);
  const live = (campaigns ?? []).filter((c) => c.active);
  const rows: Row[] = (numbers ?? []).map((n) => {
    const forward = forwardMeans(n);
    return {
      id: n.phone,
      name: n.text,
      ...(n.label ? { after: { text: ` · ${n.label}` } } : {}),
      lines: [{ text: numberMeans(n) }, ...(forward ? [{ text: forward }] : [])],
      ...(canEdit
        ? {
            onOpen: () =>
              setOpen({
                phone: n.text,
                label: n.label ?? '',
                campaignId: n.campaign?.id ?? '',
                forwardTo: n.forwardText ?? '',
                isNew: false,
              }),
            openLabel: `Change ${n.text}`,
            actions: [
              {
                id: 'remove',
                text: 'Remove',
                label: `Remove ${n.text}`,
                isDisabled: busy,
                onPress: () =>
                  write(async () => {
                    await client.call('trackingNumbers.remove', { phone: n.phone });
                    return `${n.text} removed. Its past calls keep their campaign.`;
                  }),
              },
            ],
          }
        : {}),
    };
  });
  return (
    <>
      <SettingsSection
        title="Tracking numbers"
        line="Each number’s calls are credited to its campaign from now on; calls already taken keep theirs. Add your main line too, under its own campaign, or its calls show as “No campaign”."
        {...(canEdit
          ? {
              action: (
                <Link
                  onPress={() =>
                    setOpen({
                      phone: '',
                      label: '',
                      campaignId: live[0]?.id ?? '',
                      forwardTo: '',
                      isNew: true,
                    })
                  }
                >
                  Add a number
                </Link>
              ),
            }
          : {})}
        {...noteOf({ text: said, role: 'status' }, { text: failed, role: 'alert' })}
        status={statusOf(numbers, error)}
        emptyText="No tracking numbers yet."
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        <RowList rows={rows} label="Tracking numbers" />
      </SettingsSection>
      {open && (
        <NumberSheet
          key={open.isNew ? 'new' : open.phone}
          first={open}
          campaigns={campaigns ?? []}
          onSaved={(s) => {
            setSaid(s);
            load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function NumberSheet({
  first,
  campaigns,
  onSaved,
  onClose,
}: {
  first: NumberDraft;
  campaigns: readonly Campaign[];
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const save = async () => {
    try {
      const r = await client.call<TrackingNumber>('trackingNumbers.assign', {
        phone: d.phone.trim(),
        campaignId: d.campaignId || null,
        label: d.label.trim() || null,
        forwardTo: d.forwardTo.trim() || null,
      });
      onSaved(`${r.text} saved. ${numberMeans(r)}.`);
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title={d.isNew ? 'A new tracking number' : `Change ${first.phone}`}
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      isSubmitDisabled={!d.phone.trim()}
      onClose={onClose}
      onSubmit={save}
    >
      <TextField
        label="Number"
        type="tel"
        maxLength={40}
        isReadOnly={!d.isNew}
        value={d.phone}
        onChange={(phone) => setD({ ...d, phone })}
      />
      <TextField
        label="Label"
        maxLength={100}
        placeholder="Google Ads – drains"
        value={d.label}
        onChange={(label) => setD({ ...d, label })}
      />
      <TextField
        label="Rings through to (optional)"
        type="tel"
        maxLength={40}
        placeholder="The main line when empty"
        value={d.forwardTo}
        onChange={(forwardTo) => setD({ ...d, forwardTo })}
      />
      <SelectField
        label="Calls to it are credited to"
        value={d.campaignId}
        options={[
          { value: '', label: 'No campaign' },
          ...campaigns
            // a retired campaign only when the number is under it still
            .filter((c) => c.active || c.id === d.campaignId)
            .map((c) => ({ value: c.id, label: c.active ? c.name : `${c.name} (retired)` })),
        ]}
        onChange={(campaignId) => setD({ ...d, campaignId })}
      />
    </FormSheet>
  );
}

// ── When nobody answers ──

type UnansweredDraft = { mainLine: string; ringSeconds: string; record: boolean };

function WhenNobodyAnswers({ canEdit }: { canEdit: boolean }) {
  const [now, setNow] = useState<Unanswered | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<UnansweredDraft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    const get = <T,>(key: string) => client.call<{ value: T }>('settings.get', { key }).then((r) => r.value);
    void Promise.all([
      get<string | null>('calls.mainLine'),
      get<number>('calls.ringSeconds'),
      get<boolean>('calls.recordForwarded'),
    ])
      .then(([mainLine, ringSeconds, record]) => setNow({ mainLine, ringSeconds, record }))
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  return (
    <>
      <SettingsSection
        title="When nobody answers"
        {...noteOf({ text: said, role: 'status' })}
        status={now ? 'ready' : error ? 'failed' : 'loading'}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {now && (
          <SettingRow
            name={now.mainLine ? `Main line ${phoneText(now.mainLine)}` : 'No main line'}
            help={unansweredMeans(now, phoneText)}
            control={
              canEdit ? (
                <Link
                  label="Change what happens when nobody answers"
                  onPress={() => {
                    setSaid(null);
                    setOpen({
                      mainLine: now.mainLine ? phoneText(now.mainLine) : '',
                      ringSeconds: String(now.ringSeconds),
                      record: now.record,
                    });
                  }}
                >
                  Change
                </Link>
              ) : null
            }
          />
        )}
      </SettingsSection>
      {open && (
        <UnansweredSheet
          first={open}
          onSaved={() => {
            setSaid('Saved. It applies from the next call.');
            load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function UnansweredSheet({
  first,
  onSaved,
  onClose,
}: {
  first: UnansweredDraft;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const typed = d.mainLine.trim();
  const main = typed ? normalizePhone(typed) : null;
  const save = async () => {
    if (typed && !main) throw new Error(`“${typed}” isn’t a phone number.`);
    try {
      if (main) await client.call('settings.set', { key: 'calls.mainLine', value: main });
      else await client.call('settings.clear', { key: 'calls.mainLine' });
      await client.call('settings.set', { key: 'calls.ringSeconds', value: Number(d.ringSeconds) });
      await client.call('settings.set', { key: 'calls.recordForwarded', value: d.record });
      onSaved();
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title="When nobody answers"
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      onClose={onClose}
      onSubmit={save}
    >
      <TextField
        label="Main line"
        type="tel"
        maxLength={40}
        placeholder="The office’s number"
        value={d.mainLine}
        onChange={(mainLine) => setD({ ...d, mainLine })}
      />
      <SelectField
        label="Each number rings for"
        value={d.ringSeconds}
        options={[...new Set([10, 15, 20, 25, 30, 40, 50, 60, Number(first.ringSeconds)])]
          .sort((a, b) => a - b)
          .map((s) => ({ value: String(s), label: `${s} seconds` }))}
        onChange={(ringSeconds) => setD({ ...d, ringSeconds })}
      />
      <Checkbox isSelected={d.record} onChange={(record) => setD({ ...d, record })}>
        Record answered calls, both sides (tell callers they’re recorded)
      </Checkbox>
    </FormSheet>
  );
}

// ── Blocked numbers ──

function Blocked({ canEdit }: { canEdit: boolean }) {
  type Page = { numbers: BlockedNumber[]; total: number; next: string | null };
  const [numbers, setNumbers] = useState<BlockedNumber[] | null>(null);
  const [total, setTotal] = useState(0);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(false);
  const load = useCallback(() => {
    setError(null);
    client
      .call<Page>('blockedNumbers.list', { limit: PAGE })
      .then((r) => {
        setNumbers(r.numbers);
        setTotal(r.total);
        setNext(r.next);
      })
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const { busy, said, failed, write, setSaid } = useWrite(load);
  const more = () =>
    write(async () => {
      if (!next) return '';
      const r = await client.call<Page>('blockedNumbers.list', { limit: PAGE, after: next });
      setNumbers((was) => [...(was ?? []), ...r.numbers]);
      setTotal(r.total);
      setNext(r.next);
      return '';
    });
  const rows: Row[] = (numbers ?? []).map((b) => ({
    id: b.phone,
    name: b.text,
    ...(b.note ? { lines: [{ text: b.note }] } : {}),
    ...(canEdit
      ? {
          actions: [
            {
              id: 'unblock',
              text: 'Unblock',
              label: `Unblock ${b.text}`,
              isDisabled: busy,
              onPress: () =>
                write(async () => {
                  await client.call('blockedNumbers.remove', { phone: b.phone });
                  return `${b.text} unblocked.`;
                }),
            },
          ],
        }
      : {}),
  }));
  return (
    <>
      <SettingsSection
        title="Blocked numbers"
        line={`Spam and robocalls: a call from one hears busy and isn’t kept. Never a customer’s number.${numbers ? ` ${blockedCount(total)}` : ''}`}
        {...(canEdit
          ? {
              action: (
                <Link
                  onPress={() => {
                    setSaid(null);
                    setBlocking(true);
                  }}
                >
                  Block a number
                </Link>
              ),
            }
          : {})}
        {...noteOf({ text: said || null, role: 'status' }, { text: failed, role: 'alert' })}
        // none blocked is said in the line: nothing under it
        status={numbers ? 'ready' : error ? 'failed' : 'loading'}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {rows.length > 0 && (
          <RowList rows={rows} label="Blocked" {...(next && !busy ? { onMore: more } : {})} />
        )}
      </SettingsSection>
      {blocking && (
        <BlockSheet
          onSaved={(s) => {
            setSaid(s);
            load();
          }}
          onClose={() => setBlocking(false)}
        />
      )}
    </>
  );
}

function BlockSheet({ onSaved, onClose }: { onSaved: (said: string) => void; onClose: () => void }) {
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const save = async () => {
    try {
      const r = await client.call<BlockedNumber>('blockedNumbers.add', {
        phone: phone.trim(),
        note: note.trim() || null,
      });
      onSaved(`${r.text} blocked: its calls hear busy from the next one.`);
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title="Block a number"
      submitLabel="Block"
      isDirty={!!(phone || note)}
      isSubmitDisabled={!phone.trim()}
      onClose={onClose}
      onSubmit={save}
    >
      <TextField label="Number" type="tel" maxLength={40} value={phone} onChange={setPhone} />
      <TextField
        label="Why (optional)"
        maxLength={200}
        placeholder="Robocall"
        value={note}
        onChange={setNote}
      />
    </FormSheet>
  );
}

// ── Your website ──

function Website({ canEdit, numbers }: { canEdit: boolean; numbers: readonly TrackingNumber[] }) {
  const [setup, setSetup] = useState<SiteAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<SiteDraft | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    client
      .call<SiteAnswer>('adSessions.setup', {})
      .then(setSetup)
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const snippet = setup ? tagSnippet(window.location.origin, setup.tagPath) : '';
  const textOf = (phone: string) => numbers.find((n) => n.phone === phone)?.text ?? phone;
  const copy = () => {
    setFailed(null);
    void navigator.clipboard
      ?.writeText(snippet)
      .then(() => setSaid('The tag is copied.'))
      .catch(() => setFailed('Could not copy it: select the tag and copy it.'));
  };
  const site = setup?.site;
  return (
    <>
      <SettingsSection
        title="Your website"
        line="Flowology’s tag on your site keeps each ad click, gives the call that follows its keyword, and makes your site’s forms leads with their click. Add it beside your other tags, in your tag manager or on every page."
        {...(canEdit && setup
          ? {
              action: (
                <Link
                  label="Change the website tag"
                  onPress={() => {
                    setSaid(null);
                    setOpen(siteDraftOf(setup.site));
                  }}
                >
                  Change
                </Link>
              ),
            }
          : {})}
        {...noteOf({ text: said, role: 'status' }, { text: failed, role: 'alert' })}
        status={setup ? 'ready' : error ? 'failed' : 'loading'}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {setup && site && (
          <>
            <SettingRow
              name={siteMeans(site)}
              help={[
                weekLine(setup.week),
                site.origins.length
                  ? `Sites: ${site.origins.map((o) => o.replace(/^https?:\/\//, '')).join(', ')}.`
                  : null,
              ]
                .filter(Boolean)
                .join(' ')}
              isWide
              control={
                <>
                  <TextField
                    label="The tag to add to your site"
                    multiline
                    isReadOnly
                    value={snippet}
                    onChange={() => {}}
                  />
                  <Link onPress={copy}>Copy the tag</Link>
                </>
              }
            />
            {site.numbers.length > 0 && (
              <RowList
                label="Each campaign’s number on the site"
                rows={site.numbers.map((n) => ({
                  id: n.key,
                  name: n.key,
                  lines: [{ text: `Shows ${textOf(n.phone)}` }],
                }))}
              />
            )}
          </>
        )}
      </SettingsSection>
      {open && (
        <SiteSheet
          first={open}
          numbers={numbers}
          onSaved={(a) => {
            setSetup(a);
            setSaid('Saved. Your site has it within five minutes.');
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function SiteSheet({
  first,
  numbers,
  onSaved,
  onClose,
}: {
  first: SiteDraft;
  numbers: readonly TrackingNumber[];
  onSaved: (a: SiteAnswer) => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const line = (i: number, p: Partial<SiteDraft['numbers'][number]>) =>
    setD({ ...d, numbers: d.numbers.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const save = async () => {
    const r = siteInput(d);
    if ('error' in r) throw new Error(r.error);
    try {
      onSaved(await client.call<SiteAnswer>('adSessions.setSite', r.input));
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title="Change the website tag"
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      onClose={onClose}
      onSubmit={save}
    >
      <TextField
        label="Your sites, one a line"
        multiline
        placeholder="plumber.example"
        value={d.origins}
        onChange={(origins) => setD({ ...d, origins })}
      />
      <Checkbox isSelected={d.swap} onChange={(swap) => setD({ ...d, swap })}>
        Show each campaign’s tracking number on the site. Off, it only keeps the clicks, while another tag
        shows the numbers.
      </Checkbox>
      <SettingFields label="Each campaign’s number, by the name its ads’ URLs give it">
        <p>
          Each campaign’s number, by the name its ads’ URLs give it.
          {numbers.length ? null : ' Add a tracking number first.'}
        </p>
        {d.numbers.map((n, i) => (
          <SettingFieldLine key={i} label={`Campaign ${i + 1}`}>
            <TextField
              label={`Campaign ${i + 1}, as the ads name it`}
              placeholder="water_heaters"
              maxLength={100}
              value={n.key}
              onChange={(key) => line(i, { key })}
            />
            <SelectField
              label={`Number for campaign ${i + 1}`}
              value={n.phone}
              options={[
                { value: '', label: 'Choose a tracking number' },
                ...numbers.map((t) => ({
                  value: t.phone,
                  label: t.label ? `${t.text} · ${t.label}` : t.text,
                })),
              ]}
              onChange={(phone) => line(i, { phone })}
            />
            <Link
              label={`Remove campaign ${i + 1}`}
              onPress={() => setD({ ...d, numbers: d.numbers.filter((_, j) => j !== i) })}
            >
              Remove
            </Link>
          </SettingFieldLine>
        ))}
        <Link
          isDisabled={!numbers.length}
          onPress={() => setD({ ...d, numbers: [...d.numbers, { key: '', phone: '' }] })}
        >
          Add a campaign’s number
        </Link>
      </SettingFields>
    </FormSheet>
  );
}

// ── Ad spend ──

type SpendDraft = {
  campaignId: string;
  channel: Channel;
  from: string;
  to: string;
  amount: string;
  note: string;
};

function Spend({ canEdit, campaigns }: { canEdit: boolean; campaigns: readonly Campaign[] }) {
  const [entries, setEntries] = useState<SpendEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<SpendDraft | null>(null);
  const load = useCallback(() => {
    setError(null);
    const to = isoDay(new Date());
    const from = isoDay(new Date(Date.now() - 92 * 86_400_000));
    // every page of the 92 days (list caps M11), not the first
    allSpend((input) => client.call<SpendPage<SpendEntry>>('adSpend.list', input), { from, to })
      .then(setEntries)
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const { busy, said, failed, write, setSaid } = useWrite(load);
  const live = campaigns.filter((c) => c.active);
  const today = isoDay(new Date());
  const rows: Row[] = (entries ?? []).map((e) => ({
    id: e.id,
    name: money(e.amountCents),
    lines: [{ text: spendMeans(e) }, ...(e.note ? [{ text: e.note }] : [])],
    ...(canEdit && editable(e)
      ? {
          actions: [
            {
              id: 'remove',
              text: 'Remove',
              label: `Remove ${money(e.amountCents)} ${spendMeans(e)}`,
              isDisabled: busy,
              onPress: () =>
                write(async () => {
                  await client.call('adSpend.remove', {
                    id: e.id,
                    reason: 'Removed in Settings › Campaigns',
                  });
                  return `${money(e.amountCents)} removed.`;
                }),
            },
          ],
        }
      : {}),
  }));
  return (
    <>
      <SettingsSection
        title="Ad spend"
        line="What each campaign cost, for a day or a whole month as its invoice says. The campaigns report spreads it over the days, for cost per lead and the return on ad spend. What’s imported is corrected where it came from."
        {...(canEdit
          ? {
              action: (
                <Link
                  onPress={() => {
                    setSaid(null);
                    setOpen({
                      campaignId: live[0]?.id ?? '',
                      channel: 'google_ads',
                      from: today,
                      to: today,
                      amount: '',
                      note: '',
                    });
                  }}
                >
                  Add spend
                </Link>
              ),
            }
          : {})}
        {...noteOf({ text: said, role: 'status' }, { text: failed, role: 'alert' })}
        status={statusOf(entries, error)}
        emptyText="No ad spend in the last three months."
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        <RowList rows={rows} label="Spend in the last three months" first={5} />
      </SettingsSection>
      {open && (
        <SpendSheet
          first={open}
          campaigns={live}
          onSaved={(s) => {
            setSaid(s);
            load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function SpendSheet({
  first,
  campaigns,
  onSaved,
  onClose,
}: {
  first: SpendDraft;
  campaigns: readonly Campaign[];
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const save = async () => {
    const cents = centsTyped(d.amount);
    if (cents === null) throw new Error('Type the amount in dollars, like 1,250.00.');
    try {
      await client.call('adSpend.record', {
        campaignId: d.campaignId || null,
        ...(d.campaignId ? {} : { channel: d.channel }),
        from: d.from,
        to: d.to,
        amountCents: cents,
        note: d.note.trim() || null,
      });
      onSaved(`${money(cents)} recorded.`);
    } catch (e) {
      throw fail(e);
    }
  };
  return (
    <FormSheet
      isOpen
      title="New ad spend"
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      isSubmitDisabled={!d.amount.trim()}
      onClose={onClose}
      onSubmit={save}
    >
      <SelectField
        label="Spent on"
        value={d.campaignId}
        options={[
          ...campaigns.map((c) => ({ value: c.id, label: c.name })),
          { value: '', label: 'No campaign: just a channel' },
        ]}
        onChange={(campaignId) => setD({ ...d, campaignId })}
      />
      {!d.campaignId && (
        <SelectField
          label="Channel"
          value={d.channel}
          options={CHANNELS.map((ch) => ({ value: ch, label: CHANNEL_WORDS[ch] }))}
          onChange={(v) => setD({ ...d, channel: v as Channel })}
        />
      )}
      <SettingFieldLine label="When">
        <TextField label="From" type="date" value={d.from} onChange={(from) => setD({ ...d, from })} />
        <TextField label="To" type="date" value={d.to} onChange={(to) => setD({ ...d, to })} />
      </SettingFieldLine>
      <TextField
        label="Amount"
        inputMode="decimal"
        width="number"
        placeholder="1,250.00"
        value={d.amount}
        onChange={(amount) => setD({ ...d, amount })}
      />
      <TextField
        label="Note"
        maxLength={500}
        placeholder="October’s invoice"
        value={d.note}
        onChange={(note) => setD({ ...d, note })}
      />
    </FormSheet>
  );
}

// ── Linked ad campaigns ──

interface PlatformLink {
  id: string;
  platform: Unlinked['platform'];
  platformCampaignId: string | null;
  platformCampaignName: string | null;
  campaign: { id: string; name: string };
}
const unlinkedKey = (u: Unlinked) =>
  `${u.platform}|${u.platformCampaignId ?? ''}|${u.platformCampaignName ?? ''}`;
const platformName = (l: { platformCampaignName: string | null; platformCampaignId: string | null }) =>
  l.platformCampaignName ?? l.platformCampaignId ?? 'the whole account';

function Linked({ canEdit, campaigns }: { canEdit: boolean; campaigns: readonly Campaign[] }) {
  const [links, setLinks] = useState<{ links: PlatformLink[]; unlinked: Unlinked[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<Record<string, string>>({});
  const load = useCallback(() => {
    setError(null);
    client
      .call<{ links: PlatformLink[]; unlinked: Unlinked[] }>('adCampaignLinks.list', {})
      .then(setLinks)
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const { busy, said, failed, write } = useWrite(load);
  const live = campaigns.filter((c) => c.active);
  const none = links && !links.links.length && !links.unlinked.length;
  return (
    <SettingsSection
      title="Linked ad campaigns"
      line="Which of your campaigns each Google, Microsoft or Local Services campaign is, so its spend counts there, past and future."
      {...noteOf({ text: said, role: 'status' }, { text: failed, role: 'alert' })}
      status={links ? (none ? 'empty' : 'ready') : error ? 'failed' : 'loading'}
      emptyText="No ad platform campaigns yet."
      {...(error ? { failedText: error } : {})}
      onRetry={load}
    >
      {links?.unlinked.map((u) => {
        const k = unlinkedKey(u);
        return (
          <SettingRow
            key={k}
            name={unlinkedMeans(u)}
            help="Not on a campaign yet: it counts as “No campaign”."
            control={
              canEdit ? (
                <>
                  <SelectField
                    label={`Campaign for ${unlinkedMeans(u)}`}
                    hideLabel
                    width="fit"
                    value={linking[k] ?? ''}
                    options={[
                      { value: '', label: 'Choose a campaign' },
                      ...live.map((c) => ({ value: c.id, label: c.name })),
                    ]}
                    onChange={(v) => setLinking({ ...linking, [k]: v })}
                  />
                  <Button
                    variant="secondary"
                    isDisabled={busy || !linking[k]}
                    onPress={() =>
                      write(async () => {
                        const r = await client.call<{ link: PlatformLink; moved: number }>(
                          'adCampaignLinks.set',
                          {
                            platform: u.platform,
                            platformCampaignId: u.platformCampaignId,
                            platformCampaignName: u.platformCampaignName,
                            campaignId: linking[k],
                          },
                        );
                        return `Linked to ${r.link.campaign.name}: ${r.moved} ${r.moved === 1 ? 'entry' : 'entries'} moved.`;
                      })
                    }
                  >
                    Link
                  </Button>
                </>
              ) : null
            }
          />
        );
      })}
      {links && links.links.length > 0 && (
        <RowList
          label="Linked"
          rows={links.links.map((l) => ({
            id: l.id,
            name: `${CHANNEL_WORDS[l.platform]}: ${platformName(l)}`,
            lines: [{ text: `Counts as ${l.campaign.name}` }],
            ...(canEdit
              ? {
                  actions: [
                    {
                      id: 'unlink',
                      text: 'Unlink',
                      label: `Unlink ${platformName(l)}`,
                      isDisabled: busy,
                      onPress: () =>
                        write(async () => {
                          await client.call('adCampaignLinks.remove', { id: l.id });
                          return 'Unlinked: its spend is on no campaign again.';
                        }),
                    },
                  ],
                }
              : {}),
          }))}
        />
      )}
    </SettingsSection>
  );
}

// ── Conversions to Google Ads, and how the ad accounts are doing ──

function Conversions({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState<AdsStatusProps | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [first, setFirst] = useState('');
  const [adtrackOff, setAdtrackOff] = useState(false);
  const [open, setOpen] = useState<MapDraft | null>(null);
  const load = useCallback(() => {
    setError(null);
    client
      .call<unknown>('adAccounts.status', {})
      .then((r) => {
        const p = AdsStatusProps.safeParse(r);
        if (!p.success) throw new Error('Flowology can’t show the ad accounts here.');
        setStatus(p.data);
        setFirst((f) => f || localDateOf(new Date(), p.data.timeZone));
      })
      .catch((e: unknown) => setError(say(e)));
  }, []);
  useEffect(load, [load]);
  const { busy, said, failed, write, setSaid } = useWrite(() => {
    setAdtrackOff(false);
    load();
  });
  const c = status?.conversions;
  const shown = status ? 'ready' : error ? 'failed' : 'loading';
  const actions =
    c &&
    KINDS.map((k) => {
      const a = c.actions[k];
      const ids = [a.click && `${a.click} by click`, a.call && `${a.call} by call`].filter(Boolean);
      return `${KIND_WORDS[k][0]}: ${ids.length ? ids.join(', ') : 'none'}`;
    }).join('. ') + '.';
  return (
    <>
      <SettingsSection
        title="Conversions to Google Ads"
        line="What came from a Google ad goes back to Google Ads, so its bidding learns which clicks and calls became paid work. One system sends them: turn AdTrack’s upload off before turning this on."
        {...noteOf({ text: said, role: 'status' }, { text: failed, role: 'alert' })}
        status={shown}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {c && (
          <>
            <SettingRow
              name="Send conversions to Google Ads"
              help={
                c.on
                  ? switchWords(c.on, c.from)
                  : `${switchWords(c.on, c.from)} Pick the day after AdTrack’s last upload, so no day goes twice. Each job goes to an action once, and a click AdTrack already sent is kept back.`
              }
              {...(canEdit && !c.on ? { isWide: true } : {})}
              control={
                !canEdit ? (
                  c.on ? (
                    'On'
                  ) : (
                    'Off'
                  )
                ) : c.on ? (
                  <Button
                    variant="secondary"
                    isDisabled={busy}
                    onPress={() =>
                      write(async () => {
                        await client.call('adConversions.setOn', { on: false });
                        return 'Conversions are off: nothing more goes to Google.';
                      })
                    }
                  >
                    Turn off
                  </Button>
                ) : (
                  <>
                    <SettingFields label="Turn conversions on">
                      <TextField
                        label="First day"
                        type="date"
                        width="date"
                        value={first}
                        onChange={setFirst}
                      />
                      <Checkbox isSelected={adtrackOff} onChange={setAdtrackOff}>
                        AdTrack’s upload is off
                      </Checkbox>
                    </SettingFields>
                    <Button
                      variant="primary"
                      isDisabled={busy || !adtrackOff || !first}
                      onPress={() =>
                        write(async () => {
                          const r = await client.call<{ from: string }>('adConversions.setOn', {
                            on: true,
                            from: first,
                          });
                          return `Conversions are on from ${r.from}.`;
                        })
                      }
                    >
                      {busy ? 'Turning on…' : 'Turn on'}
                    </Button>
                  </>
                )
              }
            />
            <SettingRow
              name="Conversion actions"
              help={`Each kind under its own Google Ads conversion action, by click and by call: two kinds on one action would count every job twice. A kind with none isn’t sent. ${mapFromWords(c.actionsFrom)} ${actions}`}
              control={
                canEdit ? (
                  <Link
                    label="Change the conversion actions"
                    onPress={() => {
                      setSaid(null);
                      setOpen(mapDraftOf(c.actions));
                    }}
                  >
                    Change
                  </Link>
                ) : null
              }
            />
          </>
        )}
      </SettingsSection>
      <SettingsSection
        title="How the ad accounts are doing"
        status={shown}
        {...(error ? { failedText: error } : {})}
        onRetry={load}
      >
        {status && <RowList label="How the ad accounts are doing" rows={adsHealthRows(status)} />}
        {status && status.conversions.refusals.length > 0 && (
          <RowList title="Google’s latest refusals" rows={adsRefusalRows(status)} />
        )}
      </SettingsSection>
      {open && c && (
        <ActionsSheet
          first={open}
          fromSetting={c.actionsFrom === 'setting'}
          onSaved={(s) => {
            setSaid(s);
            load();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function ActionsSheet({
  first,
  fromSetting,
  onSaved,
  onClose,
}: {
  first: MapDraft;
  fromSetting: boolean;
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [d, setD] = useState(first);
  const [backing, setBacking] = useState(false);
  const [backError, setBackError] = useState<string | null>(null);
  const save = async () => {
    const m = mapOf(d);
    if ('error' in m) throw new Error(m.error);
    try {
      await client.call('settings.set', { key: 'ads.conversionActions', value: m.value });
      onSaved('Conversion actions saved.');
    } catch (e) {
      throw fail(e);
    }
  };
  const back = () => {
    setBacking(true);
    setBackError(null);
    client
      .call('settings.set', { key: 'ads.conversionActions', value: null })
      .then(() => {
        onSaved('Conversion actions are the account’s again.');
        onClose();
      })
      .catch((e: unknown) => setBackError(say(e)))
      .finally(() => setBacking(false));
  };
  return (
    <FormSheet
      isOpen
      title="Conversion actions"
      line={KINDS.map((k) => `${KIND_WORDS[k][0]}: ${KIND_WORDS[k][1]}`).join(' ')}
      submitLabel="Save"
      isDirty={JSON.stringify(d) !== JSON.stringify(first)}
      isSubmitDisabled={backing}
      {...(backError ? { note: backError } : {})}
      onClose={onClose}
      onSubmit={save}
    >
      {KINDS.map((k) => (
        <SettingFieldLine key={k} label={KIND_WORDS[k][0]}>
          {(['click', 'call'] as const).map((m) => (
            <TextField
              key={m}
              label={`${KIND_WORDS[k][0]} by ${m}`}
              inputMode="numeric"
              placeholder="None"
              value={d[k][m]}
              onChange={(v) => setD({ ...d, [k]: { ...d[k], [m]: v } })}
            />
          ))}
        </SettingFieldLine>
      ))}
      {fromSetting && (
        <Link isDisabled={backing} onPress={back}>
          Use the account’s
        </Link>
      )}
    </FormSheet>
  );
}

// ── Monthly cap ──

function MonthlyCap({ rights }: { rights: ReturnType<typeof settingsRights> }) {
  const s = ruleSection('campaigns.spend');
  const data = useRuleData(!!s);
  if (!s) return null;
  const note = data.rows ? rulesCardLine(s.rules, rights, data.rows) : null;
  return (
    <SettingsSection
      title={s.title}
      line={s.line}
      {...(note ? { note } : {})}
      status={data.rows ? 'ready' : data.error ? 'failed' : 'loading'}
      {...(data.error ? { failedText: data.error } : {})}
      onRetry={() => void data.load()}
    >
      <SetupRuleRows rules={s.rules} data={data} rights={rights} />
    </SettingsSection>
  );
}
